/**
 * Suggested workspace secret names for Settings > Secrets.
 * LLM keys are always offered; extension keys appear when that extension is enabled.
 */

export type SecretSuggestionGroup = 'llm' | 'extension' | 'integration'

export type SecretNameSuggestion = {
  name: string
  label: string
  description: string
  group: SecretSuggestionGroup
  /** When set, only suggest if this workspace extension is enabled. */
  extensionId?: string
}

/** Canonical names Maater / extensions resolve via getSecret / BYOK. */
export const SECRET_NAME_CATALOG: SecretNameSuggestion[] = [
  {
    name: 'ANTHROPIC_API_KEY',
    label: 'Anthropic',
    description: 'Claude models (Sonnet, Haiku, Opus)',
    group: 'llm',
  },
  {
    name: 'OPENAI_API_KEY',
    label: 'OpenAI',
    description: 'GPT models and voice transcription',
    group: 'llm',
  },
  {
    name: 'GROQ_API_KEY',
    label: 'Groq',
    description: 'Fast Llama / open models',
    group: 'llm',
  },
  {
    name: 'XAI_API_KEY',
    label: 'xAI',
    description: 'Grok models via api.x.ai',
    group: 'llm',
  },
  {
    name: 'BRAVE_SEARCH_API_KEY',
    label: 'Brave Search',
    description: 'Web search tool in chat',
    group: 'integration',
  },
  {
    name: 'CURSOR_API_KEY',
    label: 'Cursor',
    description: 'Cloud agent dispatch (cursor.com dashboard)',
    group: 'extension',
    extensionId: 'cursor-cloud',
  },
  {
    name: 'ZOOM_ACCOUNT_ID',
    label: 'Zoom account',
    description: 'Zoom S2S OAuth account id',
    group: 'extension',
    extensionId: 'zoom',
  },
  {
    name: 'ZOOM_CLIENT_ID',
    label: 'Zoom client id',
    description: 'Zoom S2S OAuth client id',
    group: 'extension',
    extensionId: 'zoom',
  },
  {
    name: 'ZOOM_CLIENT_SECRET',
    label: 'Zoom client secret',
    description: 'Zoom S2S OAuth client secret',
    group: 'extension',
    extensionId: 'zoom',
  },
  {
    name: 'ZOOM_WEBHOOK_SECRET_TOKEN',
    label: 'Zoom webhook',
    description: 'HMAC validation secret from Zoom app config',
    group: 'extension',
    extensionId: 'zoom',
  },
]

export function filterSecretNameSuggestions(opts: {
  existingNames: Iterable<string>
  enabledExtensionIds?: Iterable<string>
}): SecretNameSuggestion[] {
  const existing = new Set(
    [...opts.existingNames].map((n) => String(n).toUpperCase())
  )
  const enabled = new Set(
    [...(opts.enabledExtensionIds ?? [])].map((id) => String(id).toLowerCase())
  )

  return SECRET_NAME_CATALOG.filter((item) => {
    if (existing.has(item.name)) return false
    if (!item.extensionId) return true
    // cursor-cloud OR cursor both unlock CURSOR_API_KEY
    if (item.extensionId === 'cursor-cloud') {
      return enabled.has('cursor-cloud') || enabled.has('cursor')
    }
    return enabled.has(item.extensionId)
  })
}
